import { defineStore } from 'pinia'
import { ref } from 'vue'

import type { NotificationsQuery } from '@/gql/platform-api'

type NotificationNode = NotificationsQuery['public']['notifications']['nodes'][number]

export const useNotificationCenterStore = defineStore('notificationCenter', () => {
  const notifications = ref<NotificationNode[]>([])
  const unreadCount = ref(0)
  const loading = ref(false)

  // Only a genuine load may move the badge — never an Apollo cache re-broadcast.
  const unreadCountSyncArmed = ref(true)

  function setInitial(data: NotificationsQuery['public']['notifications']) {
    setFeed(data)
    unreadCount.value = data.unreadCount
  }

  function setFeed(data: NotificationsQuery['public']['notifications']) {
    notifications.value = data.nodes
  }

  function setLoading(value: boolean) {
    loading.value = value
  }

  function reset() {
    notifications.value = []
    unreadCount.value = 0
    loading.value = false
    unreadCountSyncArmed.value = true
  }

  function applyRead(id: string) {
    const target = notifications.value.find((item) => item.id === id)
    if (!target || target.readAt) return
    notifications.value = notifications.value.map((item) => (item.id === id ? { ...item, readAt: new Date().toISOString() } : item))
    unreadCount.value = Math.max(0, unreadCount.value - 1)
  }

  function applyUnread(id: string) {
    const target = notifications.value.find((item) => item.id === id)
    if (!target || !target.readAt) return
    notifications.value = notifications.value.map((item) => (item.id === id ? { ...item, readAt: null } : item))
    unreadCount.value += 1
  }

  function setUnreadCount(value: number) {
    unreadCount.value = Math.max(0, value)
  }

  function syncUnreadCountFromQuery(value: number) {
    if (!unreadCountSyncArmed.value) return
    unreadCountSyncArmed.value = false
    unreadCount.value = Math.max(0, value)
  }

  function requestUnreadCountSync() {
    unreadCountSyncArmed.value = true
  }

  return {
    notifications,
    unreadCount,
    loading,
    setInitial,
    setFeed,
    setLoading,
    reset,
    applyRead,
    applyUnread,
    setUnreadCount,
    syncUnreadCountFromQuery,
    requestUnreadCountSync,
  }
})
